/**
 * Helpers to make zustand stores faster to write.
 *
 * @packageDocumentation
 */

import type { DynamicRequestOptions } from './Ajax';
import type { ComponentState, OrchestratableInstance, SafConfig } from '../saf';
import { WindowMode } from '../saf';
import type {
  GetState,
  SetState,
  State,
  StateCreator,
  StoreApi,
  UseStore
} from 'zustand';
import { create } from 'zustand';
import { devtools } from './zustand-middleware';
import type { IfEquals } from '../lib';

/**
 * A store capable of loading it's content through ajax.
 *
 * This interface makes it easier to write such stores by removing repetition in their typings.
 *
 */
export interface LoadingStore<Req> {
  /** Whether the store is loading */
  loading: boolean;
  /** Load new data for the store */
  load: (params: Req) => Promise<void>;
  /** If the store has been loaded before. */
  wasLoaded: boolean;
}

/**
 * A store capable of loading data for different pages.
 *
 */
export interface InfiniteScrollStore<Data, Params>
  extends LoadingStore<Params> {
  /** All the loaded pages. */
  pages: Array<Array<Data>>;
  /** Total number of loaded records. */
  loadedCount: number;
  /** Number of loaded pages. */
  loadedPageCount: number;
  /** Total number of records. */
  totalCount: number;
  /**
   * Function for loading the next page.
   * TODO: check if the options are ever even necessary when loading the next page or not.
   */
  loadNextPage: (
    options: Omit<
      Params & DynamicRequestOptions,
      'page' | 'limit' | 'setLoading' | 'url' | 'filter'
    >
  ) => Promise<void>;
}

/**
 * A store capable of loading data for different pages.
 *
 */
export interface PagingStore<Data, Params> extends LoadingStore<Params> {
  /** All the loaded pages. */
  pages: Array<Array<Data>>;
  /** Page size. */
  pageSize: number;
  /** Page index. */
  pageIndex: number;
  /** Total number of records. */
  totalCount: number;
  /**
   * Function for loading a specific page.
   */
  loadPage: (pageIndex: number) => Promise<void>;
}

/**
 * A portlet store should always contain at least the configuration, poe instance and portlet url.
 *
 */
export interface BasePortletStore<Config>
  extends SafConfig<Config>,
    Pick<ComponentState, 'active' | 'windowMode'> {
  poeInstance: OrchestratableInstance;
}

/**
 * Fixed type declaration to allow named set() calls for redux devtools
 *
 */
export type StateCreatorNamed<T extends State> = (
  set: (partial: Parameters<SetState<T>>[0], devtoolsName?: string) => void,
  get: Parameters<StateCreator<T>>[1],
  api: Parameters<StateCreator<T>>[2]
) => T;

/**
 * Create a zustand store with some added niceties by default.
 *
 * Other than the following additions, it works exactly like a {@link https://github.com/react-spring/zustand} store.
 * - Redux devtools integration {@link https://github.com/reduxjs/redux-devtools}
 * - TODO: immer integration? {@link https://github.com/react-spring/zustand#middleware}
 *
 * The redux devtools allow replaying state history, viewing the state after every action, etc.
 * To use them, set the (mandatory) 'storeName' in createStore and add the optional 'devtoolsName' when calling 'set()'.
 * The devtools integration is only enabled on pages with enabled jsDebug :)
 *
 * @param creator The store creator {@link StateCreator}
 * @param storeName The name of the store, which will be visible next to actions in the redux devtools.
 *
 */
export function createStore<T extends State>(
  storeName: string,
  creator: StateCreatorNamed<T>
): [UseStore<T>, StoreApi<T>] {
  if (window.SenseConstants?.isDebug) {
    return create(devtools(creator, storeName));
  } else {
    return create(creator);
  }
}

/**
 * Extract the properties of a store that are not already present in {@link BasePortletStore}.
 *
 */
export type StoreWithoutBasePortletStore<
  Store extends BasePortletStore<Config>,
  Config
> = Omit<Store, keyof BasePortletStore<Config>>;

/**
 * Create a portlet store where all properties of {@link BasePortletStore} are already initialized.
 *
 * Only additional properties must still be initialized in the creator function.
 *
 * @param creator the {@link StateCreator} for any additional properties in {@link Store}, or undefined if Store does not have additional properties.
 *
 */
export function createPortletStore<
  Store extends BasePortletStore<Config>,
  Config = Store['configuration'],
  StoreSuperset = StoreWithoutBasePortletStore<Store, Config>
>(
  creator: IfEquals<
    Store,
    BasePortletStore<Config>,
    undefined,
    StateCreator<StoreSuperset & State>
  >
): [UseStore<Store>, StoreApi<Store>] {
  // typescript does not understand that creator is either exactly what is needed, or undefined
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return create<Store>((set, get, api) => ({
    configuration: (undefined as unknown) as Config,
    poeInstance: (undefined as unknown) as OrchestratableInstance,
    portletId: '',
    renderDiv: '',
    url: '',
    additionalParams: null,
    windowMode: WindowMode.HIDDEN,
    active: false,
    ...creator?.(
      set as SetState<StoreSuperset & State>,
      (get as unknown) as GetState<StoreSuperset & State>,
      (api as unknown) as StoreApi<StoreSuperset & State>
    )
  }));
}
